/**
 * Component: Cart add-on script
 * ------------------------------------------------------------------------------
 * Loosely coupled scripts for the Cart add-on functionality.
 *
 * @namespace cartAddOn
 */
import axios from 'axios';
import {debounce} from 'lodash-es';
import * as cart from '@shopify/theme-cart';
import Cookies from 'js-cookie';

import cssClasses from '../helpers/cssClasses';
import {on} from '../helpers/utils';

/**
 * DOM selectors.
 */
const selectors = {
  checkbox: '[js-cart-add-on="checkbox"]',
  container: '[js-cart-add-on="container"]',
  label: '[js-cart-add-on="label"]',
  message: '[js-cart-add-on="message"]',
  textarea: '[js-cart-add-on="textarea"]',
};

/**
 * Create a new cart add-on object.
 */
export default () => {

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    checkbox: document.querySelector(selectors.checkbox),
    container: document.querySelector(selectors.container),
    label: document.querySelector(selectors.label),
    message: document.querySelector(selectors.message),
    textarea: document.querySelector(selectors.textarea),
  };

  /**
   * Initialise component.
   */
  function init() {
    setInitState();
    setEventListeners();
  }

  /**
   * Set initial state of add-on form.
   */
  function setInitState() {
    isAddOnInCart()
      .then((response) => {
        if (response) {
          setMessageActiveState();
        }
      })
      .catch((error) => {
        throw Error('Could not get response from cart', error);
      });
  }

  /**
   * Set trigger listeners.
   */
  function setEventListeners() {
    on('click', nodeSelectors.label, () => handleAddOnToggle());
    on('keyup', nodeSelectors.message, debounce(handleMessageUpdate, 500));
  }

  /**
   * Handle add on toggle events.
   * - Add/Remove existing add-on product in cart.
   * - Show/hide message if enabled in theme settings.
   */
  function handleAddOnToggle() {
    Frame.EventBus.emit('Cart:updatingPrices');

    isAddOnInCart()
      .then((response) => {
        if (!response) {
          addAddOn();
          return;
        }

        findAndRemoveAddOn();
      })
      .catch((error) => {
        throw Error('Could not get response from cart', error);
      });
  }

  /**
   * Handle text inputs on the add-on message textarea element.
   * @param {object} event - event from textarea keyup.
   */
  function handleMessageUpdate(event) {
    const message = event.target.value.trim();
    cart.updateNote(message);
  }

  /**
   * Set message state to active.
   */
  function setMessageActiveState() {
    nodeSelectors.message.classList.add(cssClasses.active);
    nodeSelectors.checkbox.checked = true;
  }

  /**
   * Reset message state to active.
   */
  function resetMessageActiveState() {
    nodeSelectors.message.classList.remove(cssClasses.active);
    nodeSelectors.textarea.value = '';
    nodeSelectors.checkbox.checked = false;
    cart.updateNote('');
  }

  /**
   * Add add-on product from theme settings to cart with validation.
   */
  function addAddOn() {
    const id = Number(nodeSelectors.container.dataset.variantId);

    cart.addItem(id, {qty: 1})
      .then(() => {
        if (theme.features.currencyConverter) {
          Frame.MultiCurrency.CurrencyConverter.convertAll(Cookies.get('new_currency'));
        }

        if (theme.features.cartAddOnMessage) {
          setMessageActiveState();
        }

        if (theme.features.freeShippingNotification) {
          Frame.EventBus.emit('FreeShippingNotification:update');
        }

        Frame.EventBus.emit('AjaxCart:addOnItemAdded');
      })
      .catch((error) => {
        throw new Error('Could not item to cart. Check product inventory.', error);
      });
  }

  /**
   * Find and remove add-on product from cart.
   */
  function findAndRemoveAddOn() {
    axios.get(`${window.Shopify.routes.root}cart.js`)
      .then((response) => {
        const product = response.data.items.find((item) => {
          return item.product_type.toLowerCase() === 'add-on';
        });
        removeAddOn(product);
      })
      .catch((error) => {
        throw new Error('Could not find ', error);
      });
  }

  /**
   * Find and remove add-on product from cart.
   * @param {Object} product - Add-on product object from cart.js.
   */
  function removeAddOn(product) {
    cart.removeItem(product.key)
      .then(() => {
        if (theme.features.currencyConverter) {
          Frame.MultiCurrency.CurrencyConverter.convertAll(Cookies.get('new_currency'));
        }

        if (theme.features.cartAddOnMessage) {
          resetMessageActiveState();
        }

        if (theme.features.freeShippingNotification) {
          Frame.EventBus.emit('FreeShippingNotification:update');
        }

        Frame.EventBus.emit('AjaxCart:addOnItemRemoved');
      })
      .catch((error) => {
        throw new Error('Could not remove add-on product from cart', error);
      });
  }

  /**
   * Check if add-on product is already in cart.
   */
  function isAddOnInCart() {
    return new Promise((resolve, reject) => {
      axios.get(`${window.Shopify.routes.root}cart.js`)
        .then((response) => {
          if (isOnlyItemInCart(response.data)) {
            resolve(false);
            return;
          }

          resolve(response.data.items.some((item) => {
            return (item.product_type.toLowerCase() === 'add-on');
          }));
        })
        .catch((error) => reject(error));
    });
  }

  /**
   * Check if the only item in cart is an add-on.
   * @param {object} data - Cart.js response data.
   */
  function isOnlyItemInCart(response) {
    const totalNumberOfItems = response.items.reduce((total, item) => {
      return (item.product_type.toLowerCase() === 'add-on') ? 0 : total + item.quantity;
    }, 0);

    if (totalNumberOfItems === 0) {
      cart.clearItems()
        .then(() => cart.clearNote())
        .then(() => Frame.EventBus.emit('AjaxCart:cartEmpty'))
        .catch((error) => {
          throw new Error('Could not clear cart while removing add-on', error);
        });
    }

    return (totalNumberOfItems === 0);
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  });
};
